import type { CSSProperties } from 'react'
import { shopifyComponentCopy } from '@testfeed/product-language'
import './ProductPairRateBar.css'

function formatRateCopy(template: string, partner: string, product: string, value: number) {
  return template.replace('{partner}', partner).replace('{product}', product).replace('{percent}', value.toFixed(1))
}

/** One scale compares a product's rate in orders with a partner and without that partner. */
export function ProductPairRateBar({
  product,
  combinedWith,
  withoutPartnerPercent,
  withPartnerPercent,
}: {
  product: string
  combinedWith: string
  withoutPartnerPercent: number | null
  withPartnerPercent: number
}) {
  const withoutPartner = withoutPartnerPercent === null ? null : Math.max(0, Math.min(withoutPartnerPercent, 100))
  const withPartner = Math.max(0, Math.min(withPartnerPercent, 100))
  const difference = withoutPartner === null ? null : withPartner - withoutPartner
  const delta = difference === null ? withPartner : Math.abs(difference)
  const sharedWidth = withoutPartner === null ? 0 : Math.min(withoutPartner, withPartner)
  const withRateLabel = formatRateCopy(shopifyComponentCopy.productPairsRateWith, combinedWith, product, withPartner)
  const withRateKey =
    withoutPartner !== null && withPartner > withoutPartner
      ? 'tf-product-pair-rate-bar__key--combined'
      : 'tf-product-pair-rate-bar__key--baseline'
  const withoutRateLabel =
    withoutPartner === null
      ? null
      : formatRateCopy(shopifyComponentCopy.productPairsRateWithout, combinedWith, product, withoutPartner)
  const withoutRateKey =
    withoutPartner !== null && withoutPartner > withPartner
      ? 'tf-product-pair-rate-bar__key--combined'
      : 'tf-product-pair-rate-bar__key--baseline'
  const comparisonLabel =
    withoutPartner === null
      ? `${product} appeared in ${withPartner.toFixed(1)}% of orders with ${combinedWith}. ${shopifyComponentCopy.productPairsRateNoWithoutOrders.replace('{partner}', combinedWith)}`
      : `${product} appeared in ${withPartner.toFixed(1)}% of orders with ${combinedWith} and ${withoutPartner.toFixed(1)}% of orders without ${combinedWith}`
  const barStyle = {
    '--tf-product-pair-baseline-width': `${sharedWidth}%`,
    '--tf-product-pair-difference-width': `${delta}%`,
  } as CSSProperties

  return (
    <s-stack gap="small-100">
      <s-text>{shopifyComponentCopy.productPairsRateOrdersWithProduct.replace('{product}', product)}</s-text>
      <div className="tf-product-pair-rate-bar" style={barStyle} role="img" aria-label={comparisonLabel}>
        {withoutPartner === null ? (
          <span className="tf-product-pair-rate-bar__segment tf-product-pair-rate-bar__difference" />
        ) : (
          <>
            <span className="tf-product-pair-rate-bar__segment tf-product-pair-rate-bar__baseline" />
            <span className="tf-product-pair-rate-bar__segment tf-product-pair-rate-bar__difference" />
          </>
        )}
      </div>
      <s-stack gap="small-100">
        {withoutPartner === null ? (
          <>
            <s-grid gridTemplateColumns="auto minmax(0, 1fr)" gap="small-100" alignItems="center">
              <span
                className="tf-product-pair-rate-bar__key tf-product-pair-rate-bar__key--difference"
                aria-hidden="true"
              />
              <s-text color="subdued">{withRateLabel}</s-text>
            </s-grid>
            <s-text color="subdued">
              {shopifyComponentCopy.productPairsRateNoWithoutOrders.replace('{partner}', combinedWith)}
            </s-text>
          </>
        ) : (
          <>
            <s-grid gridTemplateColumns="auto minmax(0, 1fr)" gap="small-100" alignItems="center">
              <span className={`tf-product-pair-rate-bar__key ${withoutRateKey}`} aria-hidden="true" />
              <s-text color="subdued">{withoutRateLabel}</s-text>
            </s-grid>
            <s-grid gridTemplateColumns="auto minmax(0, 1fr)" gap="small-100" alignItems="center">
              <span className={`tf-product-pair-rate-bar__key ${withRateKey}`} aria-hidden="true" />
              <s-text color="subdued">{withRateLabel}</s-text>
            </s-grid>
          </>
        )}
      </s-stack>
    </s-stack>
  )
}
